import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { Boxes, ExternalLink, UserRound, Workflow } from 'lucide-react'
import {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useLocation, useNavigate } from 'react-router'

import {
  ExecutionMethodBadge,
  TaskStatusBadge,
  WorkflowStatusBadge,
} from 'components/brokers/workflows/WorkflowVisualIndicators'
import {
  type TasksFilters,
  useGetTasks,
} from 'components/hooks/tasks/useGetTasks'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { SortableHeader } from 'components/reusable/table/SortableHeader'
import TanstackTable from 'components/reusable/table/TanstackTable'
import { Checkbox } from 'components/ui/checkbox'
import { getLocaleDateString } from 'utils'
import { type BrokersTask } from 'utils/web_api.schemas'

import AssignBrokersToTask from './AssignBrokersToTask'
import { createAddArrayFilter, getCarrierFilterInfo } from './utils'
import { NextUpTasksList } from '../workflows/NextUpTasksList'

type ActionVisibilityOverride = {
  openInNewTab?: boolean // Task details - always uses task ID
  openWorkflow?: boolean // Workflow details
  openBatchJob?: boolean // Batch job/operations batch details
  openMember?: boolean // Member broker portal
}

type TasksTableProps = {
  filters: TasksFilters
  setFilters?: Dispatch<SetStateAction<TasksFilters>>
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
  sorting: SortingState
  setSorting: Dispatch<SetStateAction<SortingState>>
  columnVisibilityOverrides?: Partial<
    Record<
      | keyof BrokersTask
      | 'job_type'
      | 'job_handler'
      | 'assignees'
      | 'actions'
      | 'prerequisite_tasks'
      | 'dependent_tasks'
      | 'coverage_start_date',
      boolean
    >
  >
  setColumnVisibilityOverrides?: Dispatch<
    SetStateAction<
      Partial<
        Record<
          | keyof BrokersTask
          | 'job_type'
          | 'job_handler'
          | 'assignees'
          | 'actions'
          | 'prerequisite_tasks'
          | 'dependent_tasks'
          | 'coverage_start_date',
          boolean
        >
      >
    >
  >
  actionsVisibilityOverrides?: ActionVisibilityOverride
  rowSelection?: RowSelectionState
  setRowSelection?: Dispatch<SetStateAction<RowSelectionState>>
}

const columnHelper = createColumnHelper<BrokersTask>()

const TasksTable = ({
  filters,
  setFilters,
  pagination,
  setPagination,
  sorting,
  setSorting,
  actionsVisibilityOverrides = {
    openInNewTab: true,
    openWorkflow: true,
    openBatchJob: true,
    openMember: true,
  },
  columnVisibilityOverrides,
  setColumnVisibilityOverrides,
  rowSelection = {},
  setRowSelection = () => {},
}: TasksTableProps) => {
  const { data, isLoading, isError } = useGetTasks({
    filters,
    pagination,
    sorting,
  })

  const [paginationMetadata, setPaginationMetadata] = useState<{
    total_records: number
    current_page: number
    total_pages: number
    next_page: number | null
    prev_page: number | null
  }>()

  // Try to get React Router hooks - they'll be null if we're not in a Router context
  let navigate: ReturnType<typeof useNavigate> | null = null
  let location: ReturnType<typeof useLocation> | null = null

  try {
    navigate = useNavigate()
    location = useLocation()
  } catch {
    // Not in a Router context, hooks will remain null
  }

  // Check if we're specifically in the /brokers/tasks SPA route
  // Only use React Router navigation if we're in the Tasks SPA context
  const isInTasksSPA = Boolean(
    navigate &&
    location &&
    location.pathname.startsWith('/brokers/tasks')
  )

  // Helper function to add a value to an array filter, if setFilters is provided
  const addArrayFilter = useMemo(
    () => (setFilters ? createAddArrayFilter(setFilters) : undefined),
    [setFilters]
  )

  // Memoize the columns to avoid re-rendering the table.
  const columns = useMemo(() => {
    return [
      // Selection column
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)}
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label='Select row'
          />
        ),
        enableSorting: false,
        enableHiding: false,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }: { row: Row<BrokersTask> }) => {
          return (
            <div className='flex items-center gap-4'>
              {/* Link to individual task view */}
              {actionsVisibilityOverrides?.openInNewTab && (
                <DisplayTooltipOnHover text='Open task details'>
                  <div
                    className='cursor-pointer h-full'
                    onClick={(e) => {
                      e.stopPropagation() // Prevent row click from also firing
                      if (isInTasksSPA && navigate) {
                        // Use React Router navigation only when in /brokers/tasks SPA
                        navigate(`/brokers/tasks/${row.original.id}`)
                      } else {
                        // Open in new tab when not in Tasks SPA context
                        window.open(`/brokers/tasks/${row.original.id}`, '_blank')
                      }
                    }}
                  >
                    <ExternalLink
                      size={20}
                      className='stroke-current text-gray-600'
                    />
                  </div>
                </DisplayTooltipOnHover>
              )}

              {/* Link to workflow if workflow_id is present */}
              {actionsVisibilityOverrides?.openWorkflow && row.original.workflow_id && (
                <DisplayTooltipOnHover text='Open workflow'>
                  <div
                    className='cursor-pointer h-full'
                    onClick={(e) => {
                      e.stopPropagation() // Prevent row click from also firing
                      window.open(
                        `/brokers/workflows/${row.original.workflow_id}`,
                        '_blank'
                      )
                    }}
                  >
                    <Workflow
                      size={20}
                      className='stroke-current text-gray-600'
                    />
                  </div>
                </DisplayTooltipOnHover>
              )}

              {/* Link to operations batch if batch_job_id is present */}
              {actionsVisibilityOverrides?.openBatchJob && row.original.batch_job_id && (
                <DisplayTooltipOnHover text='Open recurring job details'>
                  <div
                    className='cursor-pointer h-full'
                    onClick={(e) => {
                      e.stopPropagation() // Prevent row click from also firing
                      window.open(
                        `/brokers/operations_batches/${row.original.batch_job_id}`,
                        '_blank'
                      )
                    }}
                  >
                    <Boxes
                      size={20}
                      className='stroke-current text-gray-600'
                    />
                  </div>
                </DisplayTooltipOnHover>
              )}

              {/* Link to broker portal if member_id is present */}
              {actionsVisibilityOverrides?.openMember && row.original.member_id && (
                <DisplayTooltipOnHover text='Open member broker portal'>
                  <div
                    className='cursor-pointer h-full'
                    onClick={(e) => {
                      e.stopPropagation() // Prevent row click from also firing
                      window.open(
                        `/brokers/members/${row.original.member_id}`,
                        '_blank'
                      )
                    }}
                  >
                    <UserRound
                      size={20}
                      className='stroke-current text-gray-600'
                    />
                  </div>
                </DisplayTooltipOnHover>
              )}
            </div>
          )
        },
      }),
      columnHelper.accessor('created_at', {
        header: (info) => (
          <SortableHeader<BrokersTask> {...info} headerText='Date' />
        ),
        cell: (info) => getLocaleDateString(new Date(info.getValue())),
        enableSorting: true,
      }),
      columnHelper.accessor('member_name', {
        header: 'Member',
        cell: (info) => {
          const memberName = info.getValue()
          const memberId = info.row.original.member_id

          if (!memberName) return '-'

          return (
            <span
              className={addArrayFilter ? 'cursor-pointer hover:underline' : ''}
              onClick={
                addArrayFilter
                  ? (e) => {
                      e.stopPropagation()
                      if (memberId) {
                        addArrayFilter('member_ids', memberId)
                      }
                    }
                  : undefined
              }
            >
              {memberName}
            </span>
          )
        },
      }),
      columnHelper.accessor('carrier_name', {
        header: 'Carrier',
        cell: (info) => {
          const carrierName = info.getValue()
          if (!carrierName) return '-'

          return (
            <span
              className={addArrayFilter ? 'cursor-pointer hover:underline' : ''}
              onClick={
                addArrayFilter
                  ? (e) => {
                      e.stopPropagation()
                      const { key, value } = getCarrierFilterInfo(carrierName)
                      addArrayFilter(key, value)
                    }
                  : undefined
              }
            >
              {carrierName}
            </span>
          )
        },
      }),
      columnHelper.accessor('coverage_start_date', {
        header: 'Coverage Start',
        cell: (info) => {
          const date = info.getValue()
          if (!date) return '-'
          return getLocaleDateString(new Date(date))
        },
      }),
      columnHelper.accessor('brokers', {
        id: 'assignees',
        header: 'Assignees',
        cell: ({ getValue, row }) => {
          const { id } = row.original
          return (
            <AssignBrokersToTask id={id} assignedBrokers={getValue() || []} />
          )
        },
      }),
      columnHelper.accessor('status', {
        header: (info) => (
          <SortableHeader<BrokersTask> {...info} headerText='Status' />
        ),
        cell: (info) => {
          const status = info.getValue()
          return (
            <TaskStatusBadge
              status={status}
              isExecutable={info.row.original.can_execute}
              onClick={
                addArrayFilter
                  ? (e: React.MouseEvent) => {
                      e.stopPropagation()
                      addArrayFilter('statuses', status)
                    }
                  : undefined
              }
            />
          )
        },
        enableSorting: true,
      }),
      columnHelper.accessor('execution_method', {
        header: (info) => (
          <SortableHeader<BrokersTask>
            {...info}
            headerText='Execution Method'
          />
        ),
        cell: (info) => {
          const method = info.getValue()
          return (
            <ExecutionMethodBadge
              method={method}
              onClick={
                method && addArrayFilter
                  ? (e: React.MouseEvent) => {
                      e.stopPropagation()
                      addArrayFilter('execution_methods', method)
                    }
                  : undefined
              }
            />
          )
        },
        enableSorting: true,
      }),
      columnHelper.accessor('workflow_status', {
        header: 'Workflow Status',
        cell: (info) => {
          const status = info.getValue()

          if (!status) return '-'

          return <WorkflowStatusBadge status={status} />
        },
      }),
      columnHelper.accessor('display_name', {
        id: 'task_type',
        header: 'Task Type',
        cell: (info) => {
          const displayName = info.getValue()
          const taskType = info.row.original.task_type

          if (!displayName) return '-'

          return (
            <span
              className={addArrayFilter ? 'cursor-pointer hover:underline' : ''}
              onClick={
                addArrayFilter
                  ? (e) => {
                      e.stopPropagation()
                      if (taskType) {
                        addArrayFilter('task_types', taskType)
                      }
                    }
                  : undefined
              }
            >
              {displayName}
            </span>
          )
        },
      }),
      columnHelper.accessor('job.type', {
        id: 'job_type',
        header: 'Job Type',
      }),
      columnHelper.accessor('job.description', {
        id: 'job_handler',
        header: 'Handler',
      }),
      columnHelper.accessor('message', {
        id: 'message',
        header: 'Message',
        cell: (info) => (
          <div className='text-sm max-w-[400px] truncate'>
            {info.getValue()}
          </div>
        ),
      }),
      columnHelper.accessor('prerequisite_tasks_summary', {
        id: 'prerequisite_tasks',
        header: 'Dependent Task(s)',
        cell: (info) => {
          const tasks = info.getValue()
          return <NextUpTasksList tasks={tasks ?? []} maxVisible={2} />
        },
      }),
      columnHelper.accessor('dependent_tasks_summary', {
        id: 'dependent_tasks',
        header: 'Next Task(s)',
        cell: (info) => {
          const tasks = info.getValue()
          return <NextUpTasksList tasks={tasks ?? []} maxVisible={2} />
        },
      }),
    ]
  }, [actionsVisibilityOverrides, addArrayFilter])

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    state: {
      pagination,
      sorting,
      columnVisibility: columnVisibilityOverrides,
      rowSelection,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibilityOverrides,
    enableRowSelection: true,
    enableHiding: true,
    getRowId: (row) => row.id,
    manualPagination: true,
    manualSorting: true,
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data.pagination)
    }
  }, [data?.pagination])

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={isError}
      noDataMessage='There are no tasks that need attention.'
      setSorting={setSorting}
      defaultSortingState={[]}
      paginationLabelSingular='task'
      paginationMetadata={paginationMetadata}
      tableMessageHeightClass='h-32'
      onNextPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.next_page || 0,
        })}
      onPreviousPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.prev_page || 0,
        })}
    />
  )
}

export default TasksTable
